import { IProductVariant } from '../_models/product-variant';

export type MaterialNumberSelectionResolution = {
  materialNumber: string | undefined;
  materialNumberForStorage: string;
};

export type ResolveMaterialNumberForTypeCodeSelectionParams = {
  selectedMaterialNumber: string | undefined;
  storedMaterialNumber: string;
  uniqueMaterialNumbers: string[];
  canReuseStoredMaterialNumber: boolean;
  clearMaterialNumberValue: string | undefined;
};

export type StoredTypeCodeMaterialCandidate = {
  typeCode: string;
  materialNumber: string;
};

export function getStoredMaterialNumberForTypeCode(
  selectedTypeCode: string,
  candidates: StoredTypeCodeMaterialCandidate[]
): string {
  const normalizedSelectedTypeCode = (selectedTypeCode || '').trim().toUpperCase();

  for (const candidate of candidates) {
    const normalizedCandidateTypeCode = (candidate.typeCode || '').trim().toUpperCase();
    if (normalizedCandidateTypeCode === normalizedSelectedTypeCode) {
      return candidate.materialNumber || '';
    }
  }

  return '';
}

export function getUniqueMaterialNumbersForTypeCode(
  variants: IProductVariant[],
  selectedTypeCode: string
): string[] {
  const matchingMaterialNumbers = variants
    .filter((variant: IProductVariant) => variant.typecode === selectedTypeCode)
    .map((variant: IProductVariant) => variant.materialnumber)
    .filter((materialNumber): materialNumber is string => !!materialNumber);

  return Array.from(new Set(matchingMaterialNumbers));
}

export function resolveMaterialNumberForTypeCodeSelection(
  params: ResolveMaterialNumberForTypeCodeSelectionParams
): MaterialNumberSelectionResolution {
  const {
    selectedMaterialNumber,
    storedMaterialNumber,
    uniqueMaterialNumbers,
    canReuseStoredMaterialNumber,
    clearMaterialNumberValue
  } = params;

  let materialNumber: string | undefined = selectedMaterialNumber;
  let materialNumberForStorage = '';

  const normalizedStoredMaterialNumber = (storedMaterialNumber || '').trim().toUpperCase();

  // First guard: re-selecting the same typecode should keep a matching stored material number.
  if (canReuseStoredMaterialNumber && normalizedStoredMaterialNumber) {
    materialNumber = storedMaterialNumber;
    materialNumberForStorage = storedMaterialNumber;
  }

  const normalizedUniqueMaterialNumbers = uniqueMaterialNumbers.map((materialNumberValue) => materialNumberValue.trim().toUpperCase());

  if (uniqueMaterialNumbers.length === 1) {
    materialNumber = uniqueMaterialNumbers[0];
    materialNumberForStorage = materialNumber;
  } else if (canReuseStoredMaterialNumber && normalizedStoredMaterialNumber && (
    normalizedUniqueMaterialNumbers.length === 0 ||
    normalizedUniqueMaterialNumbers.includes(normalizedStoredMaterialNumber)
  )) {
    // For ambiguous mappings keep a previously stored material number.
    // If backend did not provide material numbers in this response, do not clear a valid stored value.
    materialNumber = storedMaterialNumber || undefined;
    materialNumberForStorage = materialNumber ?? '';
  } else {
    materialNumber = clearMaterialNumberValue;
    materialNumberForStorage = '';
  }

  return { materialNumber, materialNumberForStorage };
}
